import React, { useCallback, useEffect } from 'react';
import { Grid, Box, Typography, useMediaQuery, IconButton } from '@mui/material';
import { ReviewCard } from './Cards/ReviewCard';
import { ITaentReviewResponse, ITalentReview } from '../../types/global/helper';
import AddIcon from '@mui/icons-material/Add';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getJWtDetails, useDispatchPromise } from '../../utils/AppUtils';
import { useThemeContext } from '../../hook/useThemeManager';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { currentTalentReviewsSelector } from '../../selectors/selectors';
import { getTalentReviews } from '../../redux/talentProfile/action';
import { Spinner } from '@chakra-ui/react';
import { Pagination } from '../BuzzBridge/components/Pagination';
import { usePagination } from '../../hook/usePagination';

export interface IReviews {
    reviews?: ITalentReview[];
    profileUserId?: string;
    onAddReview?: () => void;
    onEditReview?: (review: ITalentReview) => void;
    onDeleteReview?: (review: ITalentReview) => void;
    onLikeReview?: (review: ITalentReview) => void;
    onDislikeReview?: (review: ITalentReview) => void;
    onDeleteLikeReview?: (likeId: string, review: ITalentReview) => void;
}

const ROW = 6;

export const Reviews: React.FC<IReviews> = ({
    profileUserId,
    onAddReview,
    onEditReview,
    onDeleteReview,
    onLikeReview,
    onDislikeReview,
    onDeleteLikeReview,
}) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const { themeStyle } = useThemeContext();
    const { loading, reviews } = useSelector(currentTalentReviewsSelector);
    const dispatch = useDispatchPromise();
    const { user_id } = getJWtDetails();
    const currentUserId = user_id;
    const canAddReview = profileUserId && currentUserId && profileUserId !== currentUserId;
    const { handlePageChange, pagination, setPagination } = usePagination(1, ROW);
    const getUserReviews = useCallback(() => {
        if (profileUserId) {
            dispatch(
                getTalentReviews({
                    talentId: profileUserId,
                    limit: pagination.rows || ROW,
                    page: pagination.page || 1,
                }),
            ).then(response => {
                const load: ITaentReviewResponse = response as ITaentReviewResponse;
                const { currentPage, total, totalPages } = load;
                setPagination({
                    page: currentPage,
                    rows: ROW,
                    totalCount: total,
                    totalPages: totalPages,
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileUserId, pagination.page, pagination.rows]);

    useEffect(() => {
        getUserReviews();
    }, [getUserReviews]);

    return (
        <Grid
            container
            spacing={isMobile ? 2 : 3}
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                padding: 2,
            }}>
            {canAddReview && (
                <Grid item xs={12}>
                    <Box
                        onClick={onAddReview}
                        sx={{
                            width: {
                                xs: '100%',
                                sm: 320,
                            },
                            minHeight: 180,
                            borderRadius: 3,
                            boxShadow: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            border: `1.5px dashed ${theme[themeStyle].highlight}`,
                        }}>
                        <IconButton sx={{ color: theme.dark.highlight, mb: 1 }}>
                            <AddIcon sx={{ fontSize: 48, color: theme[themeStyle].tag_white }} />
                        </IconButton>
                        <Typography
                            sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: theme[themeStyle].tag_white }}>
                            Add Review
                        </Typography>
                    </Box>
                </Grid>
            )}
            {loading && (
                <Box sx={{ width: '100%', justifyItems: 'center', alignItems: 'center' }}>
                    <Spinner height={10} w={10} sx={{ display: 'flex' }} color={theme[themeStyle].tag_white} />
                </Box>
            )}
            {!!reviews &&
                !loading &&
                !isEmpty(reviews) &&
                reviews.map((review, index) => {
                    const canEditDelete = review.user?._id === currentUserId;
                    return (
                        <Grid item xs={12} key={index}>
                            <ReviewCard
                                review={review}
                                canEditDelete={canEditDelete}
                                onEdit={onEditReview ? () => onEditReview(review) : undefined}
                                onDelete={onDeleteReview ? () => onDeleteReview(review) : undefined}
                                onLike={onLikeReview ? () => onLikeReview(review) : undefined}
                                onDislike={onDislikeReview ? () => onDislikeReview(review) : undefined}
                                onDeleteLike={
                                    onDeleteLikeReview ? likeId => onDeleteLikeReview(likeId, review) : undefined
                                }
                            />
                        </Grid>
                    );
                })}
            <Box sx={{ width: '100%', m: 3 }}>
                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages || 0}
                    onPageChange={handlePageChange}
                />
            </Box>
        </Grid>
    );
};
